import React, { useState, useRef, useEffect } from 'react';
import translate from '../../i18n/translate';

/**
 * Roadmap — horizontal scrollable timeline with expand/collapse cards.
 * One card open at a time. Default: the "current" phase is expanded so
 * the user lands on the most relevant info without clicking.
 *
 * The container has no panel background — sits directly on the page bg
 * (DNA texture / theme color), like Vision and FundingCrisis.
 */

// Phases re-aligned 2026-05-13. Dates reflect what actually shipped vs
// what's queued. Mainnet deliberately has NO date — gated on the 4 audits
// (legal, BARDA-method, code, financial) + entity formation, see Roadmap v2
// Section 2. Same for DVBC Phase 3 — depends on first real research grants
// being executed under the framework.
const PHASES = [
  { year: '2018 – 2024', status: 'done',    titleKey: 'roadmap-1-2',  notes: ['roadmap-1-3'] },
  { year: '2025',        status: 'done',    titleKey: 'roadmap-9-1',  notes: ['roadmap-9-2', 'roadmap-9-3', 'roadmap-9-4'] },
  { year: '2026',        status: 'current', titleKey: 'roadmap-10-1', notes: ['roadmap-10-2', 'roadmap-10-3', 'roadmap-10-4'] },
  { year: '2026',        status: 'current', titleKey: 'roadmap-11-1', notes: ['roadmap-11-2', 'roadmap-11-3'] },
  { year: '2026 – 2027', status: 'next',    titleKey: 'roadmap-13-1', notes: ['roadmap-13-2', 'roadmap-13-3'] },
  { year: 'TBD',         status: 'next',    titleKey: 'roadmap-12-1', notes: ['roadmap-12-2', 'roadmap-12-3'] },
  { year: 'TBD',         status: 'next',    titleKey: 'roadmap-14-1', notes: ['roadmap-14-2', 'roadmap-14-3'] },
];

const STATUS_LABEL = {
  done:    'shipped',
  current: 'live',
  next:    'planned',
};

export default function Roadmap() {
  // Default expand: first "current" phase, or 0 if none
  const defaultIdx = PHASES.findIndex(p => p.status === 'current');
  const [openIdx, setOpenIdx] = useState(defaultIdx >= 0 ? defaultIdx : 0);
  const [trackOffset, setTrackOffset] = useState(0);
  const railRef = useRef(null);
  const trackRef = useRef(null);
  const itemRefs = useRef([]);

  // Centre the active step in the rail by translating the track. CSS handles
  // the transition timing — the JS just supplies the target offset.
  // No clamping: the active item ALWAYS lands at the visual centre of the
  // rail, even if that means an empty gap appears at the leading or trailing
  // edge (which is exactly the desired UX).
  const recenter = () => {
    const rail = railRef.current;
    const target = itemRefs.current[openIdx];
    if (!rail || !target) return;
    const railW = rail.offsetWidth;
    const offset = railW / 2 - target.offsetLeft - target.offsetWidth / 2;
    setTrackOffset(offset);
  };

  // First render: centre immediately, no waiting (otherwise the user would
  // see an uncentred state for half a second on load).
  // Subsequent clicks: let the card expand first (~420ms CSS transition),
  // then centre. This gives the natural flow the user asked for —
  // text unfolds, then everything glides into place.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      recenter();
      return;
    }
    const id = setTimeout(recenter, 460);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIdx]);

  // Recentre on viewport resize so the active card stays centred.
  useEffect(() => {
    const onResize = () => recenter();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIdx]);

  return (
    <section className="lv2-section lv2-section--roadmap" id="roadmap" style={{ paddingTop: 0 }}>
      <div className="lv2-shell">
        <div className="lv2-section-head">
          <span className="lv2-section-tag">{translate('roadmap-ticker')}</span>
          <h2>{translate('roadmap-title')}</h2>
          <p className="sub">{translate('roadmap-sub')}</p>
        </div>
      </div>

      <div className="lv2-rmap-wrap">
        <div className="lv2-rmap-rail" ref={railRef}>
          <div
            className="lv2-rmap-track"
            ref={trackRef}
            style={{ transform: `translate3d(${trackOffset}px, 0, 0)` }}
          >
            {/* The horizontal line connecting all phase markers */}
            <div className="lv2-rmap-line" aria-hidden="true" />

            {PHASES.map((p, idx) => {
              const isOpen = idx === openIdx;
              return (
                <button
                  key={idx}
                  ref={el => (itemRefs.current[idx] = el)}
                  type="button"
                  className={
                    'lv2-rmap-step' +
                    ' lv2-rmap-step--' + p.status +
                    (isOpen ? ' is-open' : '')
                  }
                  onClick={() => setOpenIdx(idx)}
                  aria-expanded={isOpen}
                >
                  <span className="lv2-rmap-marker">
                    <span className={'lv2-rmap-mdot lv2-rmap-mdot--' + p.status} />
                  </span>
                  <span className="lv2-rmap-step-year">{p.year}</span>
                  <span className="lv2-rmap-step-title">{translate(p.titleKey)}</span>
                  <span className={'lv2-rmap-step-pill lv2-rmap-step-pill--' + p.status}>
                    {STATUS_LABEL[p.status]}
                  </span>
                  <div className="lv2-rmap-step-body" aria-hidden={!isOpen}>
                    <div className="lv2-rmap-step-body-inner">
                      <ul className="lv2-rmap-step-notes">
                        {p.notes.map((k, i) => (
                          <li key={i}>{translate(k)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Soft fade at edges so scroll feels natural */}
        <div className="lv2-rmap-fade lv2-rmap-fade--l" aria-hidden="true" />
        <div className="lv2-rmap-fade lv2-rmap-fade--r" aria-hidden="true" />
      </div>
    </section>
  );
}
